import { Controller } from "@hotwired/stimulus"

/**
 * Adaptive Sync Controller
 * Gestiona la sincronización adaptativa de tickets según batería y visibilidad
 * 
 * Features:
 * - Pausa updates cuando la app está en background (Page Visibility API)
 * - Cambia a polling cuando batería < 20% (Battery API)
 * - Usa Turbo Streams nativos (no crea conexiones manuales)
 */
export default class extends Controller {
  static values = {
    pollingInterval: { type: Number, default: 30000 }, // 30s por defecto
    syncCheckUrl: String
  }

  connect() {
    console.log("🔄 Adaptive Sync Controller conectado")
    
    this.isVisible = !document.hidden
    this.isBatteryLow = false
    this.pollingTimer = null
    this.lastSyncTimestamp = null

    this.setupVisibilityListener()
    this.setupBatteryMonitoring()
    this.evaluateSyncStrategy()
  }

  disconnect() {
    console.log("🔄 Adaptive Sync Controller desconectado")
    this.stopPolling()
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
  }

  /**
   * Page Visibility API - detecta cuando la app está en background
   */
  setupVisibilityListener() {
    this.handleVisibilityChange = () => {
      this.isVisible = !document.hidden
      console.log(`👁️ Visibility cambió: ${this.isVisible ? 'visible' : 'hidden'}`)
      this.evaluateSyncStrategy()
    }
    
    document.addEventListener("visibilitychange", this.handleVisibilityChange)
  }

  /**
   * Battery API - detecta batería baja para cambiar a polling
   */
  async setupBatteryMonitoring() {
    if (!('getBattery' in navigator)) {
      console.warn("⚠️ Battery API no disponible")
      return
    }

    try {
      const battery = await navigator.getBattery()
      
      this.updateBatteryStatus = () => {
        const level = battery.level * 100
        const isCharging = battery.charging
        const wasLow = this.isBatteryLow
        
        // Batería baja = < 20% y no cargando
        this.isBatteryLow = level < 20 && !isCharging
        
        if (this.isBatteryLow !== wasLow) {
          console.log(`🔋 Batería ${level.toFixed(0)}% ${isCharging ? '(cargando)' : ''} - ${this.isBatteryLow ? 'MODO POLLING' : 'MODO TURBO'}`)
          this.evaluateSyncStrategy()
        }
      }

      battery.addEventListener("levelchange", this.updateBatteryStatus)
      battery.addEventListener("chargingchange", this.updateBatteryStatus)
      
      this.updateBatteryStatus() // Check inicial
    } catch (error) {
      console.error("❌ Error al acceder a Battery API:", error)
    }
  }

  /**
   * Evalúa qué estrategia de sync usar basada en visibilidad y batería
   */
  evaluateSyncStrategy() {
    // Si está en background, pausar todo
    if (!this.isVisible) {
      console.log("⏸️ App en background - pausando sync")
      this.stopPolling()
      return
    }

    // Si batería baja, activar polling
    if (this.isBatteryLow) {
      console.log("🔋 Batería baja - activando polling")
      this.startPolling()
    } else {
      console.log("⚡ Usando Turbo Streams nativo")
      this.stopPolling()
      // Turbo Streams sigue funcionando automáticamente
    }
  }

  /**
   * Inicia polling manual (solo cuando batería < 20%)
   */
  startPolling() {
    if (this.pollingTimer) return // Ya está activo

    console.log(`🔄 Polling iniciado (cada ${this.pollingIntervalValue / 1000}s)`)
    
    this.pollingTimer = setInterval(() => {
      this.fetchUpdates()
    }, this.pollingIntervalValue)

    // Primera fetch inmediata
    this.fetchUpdates()
  }

  /**
   * Detiene polling
   */
  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
      console.log("⏹️ Polling detenido")
    }
  }

  /**
   * Fetch updates desde el endpoint sync_check
   */
  async fetchUpdates() {
    if (!this.hasSyncCheckUrlValue) {
      console.warn("⚠️ No se configuró syncCheckUrl")
      return
    }

    try {
      const url = new URL(this.syncCheckUrlValue, window.location.origin)
      if (this.lastSyncTimestamp) {
        url.searchParams.set('since', this.lastSyncTimestamp)
      }

      console.log(`🔍 Fetching updates desde ${url.pathname}`)

      const response = await fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const turboStreamHTML = await response.text()
      
      if (turboStreamHTML.trim().length > 0) {
        console.log("📦 Updates recibidos, aplicando Turbo Streams")
        Turbo.renderStreamMessage(turboStreamHTML)
        this.lastSyncTimestamp = new Date().toISOString()
      } else {
        console.log("✅ Sin cambios desde último sync")
      }

    } catch (error) {
      console.error("❌ Error en fetchUpdates:", error)
    }
  }

  /**
   * Forzar sync manual (llamado desde UI si es necesario)
   */
  forceSync() {
    console.log("🔄 Sync manual forzado")
    this.lastSyncTimestamp = null // Reset para traer todos los cambios
    this.fetchUpdates()
  }
}
